import React from 'react';

/**
 * Student ID Card — CR80 portrait (54mm × 85.6mm).
 *
 * Visual reference: organic teal + orange wave borders hugging all four edges,
 * horizontal logo + center-name header, square orange-bordered photo, big teal
 * student name, left-aligned details with aligned colons, minimal footer with
 * an orange underline accent.
 */
const FALLBACK_LOGO =
  'https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/h002c2co_1%20copy.png';

const ORANGE = '#F58A2A';
const ORANGE_SOFT = '#FFA45C';
const TEAL = '#16A09A';
const TEAL_DARK = '#0F7A75';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return '—';
  }
};

/**
 * Wave/blob border — coordinates in card-millimetres (54 × 85.6).
 * Layout faithful to the approved reference:
 *  TOP-LEFT  : medium TEAL blob with tiny ORANGE peek at the very corner
 *  TOP-RIGHT : medium TEAL accent blob (similar size to top-left)
 *  RIGHT     : large ORANGE wave from upper-right flowing all the way down
 *  BOTTOM-LEFT : large organic ORANGE blob taking ~35% of bottom-left
 *  LEFT-MID  : small TEAL accent dot + decorative dots
 */
const WaveBorder = () => (
  <svg
    viewBox="0 0 54 85.6"
    preserveAspectRatio="none"
    style={{
      position: 'absolute', inset: 0,
      width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 0,
    }}
  >
    {/* === TOP-LEFT — tiny ORANGE peek (corner only) === */}
    <path
      d="M 0 0 L 6 0 C 5 4, 6 7, 3 9 C 1.5 10, 0 9, 0 7 Z"
      fill={ORANGE}
    />

    {/* === TOP-LEFT — medium TEAL blob (organic, irregular) === */}
    <path
      d="
        M 0 0
        L 18 0
        C 16 4, 20 8, 16 12
        C 12 15, 14 19, 10 21
        C 6 22, 3 19, 0 19
        Z"
      fill={TEAL}
    />

    {/* === TOP-RIGHT — medium TEAL blob (mirrors top-left in scale) === */}
    <path
      d="
        M 54 0
        L 38 0
        C 40 4, 36 7, 39 11
        C 42 14, 47 12, 50 14
        C 52 15, 53 13, 54 14
        Z"
      fill={TEAL}
    />

    {/* === RIGHT SIDE — large ORANGE flowing wave (top → bottom) === */}
    <path
      d="
        M 54 8
        C 50 14, 52 22, 47 28
        C 43 34, 49 42, 46 50
        C 43 58, 48 66, 45 74
        C 43 80, 48 84, 54 85.6
        L 54 8
        Z"
      fill={ORANGE}
    />

    {/* === RIGHT SIDE — soft orange highlight under-layer === */}
    <path
      d="
        M 54 22
        C 51 30, 53 38, 50 46
        C 47 54, 52 62, 49 70
        C 47 76, 52 82, 54 85
        L 54 22
        Z"
      fill={ORANGE_SOFT}
      opacity="0.5"
    />

    {/* === LEFT-MID — small TEAL accent === */}
    <path
      d="M 0 38 C 3 39, 4 43, 2 47 C 1 49, 0 47, 0 44 Z"
      fill={TEAL}
      opacity="0.85"
    />

    {/* === BOTTOM-LEFT — large organic ORANGE blob === */}
    <path
      d="
        M 0 56
        C 5 57, 9 61, 7 67
        C 5 72, 11 76, 8 82
        C 6 84, 10 85, 8 85.6
        L 0 85.6
        Z"
      fill={ORANGE}
    />

    {/* === BOTTOM-LEFT — small TEAL lobe at the very corner === */}
    <path
      d="M 0 80 C 3 81, 4 84, 2 85.6 L 0 85.6 Z"
      fill={TEAL}
      opacity="0.85"
    />

    {/* === Decorative dots === */}
    <circle cx="13" cy="26" r="0.6" fill={ORANGE} opacity="0.65" />
    <circle cx="9"  cy="36" r="0.5" fill={ORANGE} opacity="0.55" />
    <circle cx="46" cy="80" r="0.6" fill={TEAL}   opacity="0.7" />
    <circle cx="48" cy="3"  r="0.6" fill={ORANGE} opacity="0.6" />
  </svg>
);

export const StudentIdCard = ({ data, scale = 1 }) => {
  if (!data || !data.student) return null;
  const { student, course, center } = data;
  const logoSrc = (center && (center.logo_url || center.logo)) || FALLBACK_LOGO;
  const initial = (student.name || '?').charAt(0).toUpperCase();
  // Auto-shrink long names so they never get cut. Tuned for the available
  // ~38mm of horizontal space inside the card content area.
  const nameLen = (student.name || '').length;
  const nameFontSize = nameLen > 22 ? '2.8mm' : nameLen > 16 ? '3.3mm' : '3.8mm';

  return (
    <div
      className="iqra-student-card"
      style={{
        width: '54mm',
        height: '85.6mm',
        position: 'relative',
        backgroundColor: '#ffffff',
        borderRadius: '3mm',
        overflow: 'hidden',
        fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
        boxShadow: '0 0.5mm 2mm rgba(0,0,0,0.12)',
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: 'top left',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <WaveBorder />

      {/* Content layer */}
      <div
        style={{
          position: 'relative', zIndex: 2,
          height: '100%',
          display: 'flex', flexDirection: 'column',
          padding: '3mm 8mm 3mm 4mm', // tight padding; bottom-3mm allows blob room without clipping
        }}
      >
        {/* Logo absolute top-left + Institute name large centered (matches reference) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '0.5mm', minHeight: '14mm' }}>
          <img
            src={logoSrc}
            alt="Logo"
            style={{
              position: 'absolute', top: '2mm', left: '2mm',
              width: '13mm', height: '13mm', borderRadius: '50%',
              objectFit: 'contain', backgroundColor: '#fff',
              border: `0.6mm solid ${ORANGE}`,
              padding: '0.4mm',
              boxShadow: '0 0.3mm 0.6mm rgba(0,0,0,0.18)',
              zIndex: 3,
            }}
          />
          <div
            style={{
              fontSize: '4.6mm', fontWeight: 900,
              color: TEAL_DARK, letterSpacing: '0.1mm',
              lineHeight: 1.05,
              textTransform: 'uppercase',
              textAlign: 'center',
              wordBreak: 'break-word',
              padding: '0 1mm',
              maxWidth: '34mm',  // keeps name within white space between left logo + right wave
              marginLeft: '13mm', // shift right so it doesn't sit under logo
            }}
          >
            {center?.center_name || 'Iqra Skills'}
          </div>
        </div>

        {/* STUDENT ID heading — smaller, sits just below institute name (NO id number here) */}
        <div style={{ textAlign: 'center', marginTop: '1.5mm' }}>
          <div
            style={{
              fontSize: '4mm', fontWeight: 900,
              color: ORANGE, letterSpacing: '0.5mm',
              lineHeight: 1,
            }}
          >
            STUDENT ID
          </div>
        </div>

        {/* Photo — square with orange frame (slightly compact to fit 5 detail rows) */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5mm' }}>
          <div
            style={{
              width: '22mm', height: '25mm',
              border: `0.8mm solid ${ORANGE}`,
              backgroundColor: '#cfe9f1',
              overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {student.photo ? (
              <img
                src={student.photo}
                alt={student.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: '12mm', color: TEAL_DARK, fontWeight: 800 }}>
                {initial}
              </span>
            )}
          </div>
        </div>

        {/* Student name — auto-shrink for long names so it never gets cut */}
        <div
          style={{
            textAlign: 'center', marginTop: '1.8mm',
            fontSize: nameFontSize,
            fontWeight: 900,
            color: TEAL_DARK, letterSpacing: '0.1mm',
            textTransform: 'uppercase', lineHeight: 1.1,
            wordBreak: 'break-word',
            paddingLeft: '6mm', paddingRight: '6mm', // balanced L/R so name visually centers between waves
          }}
        >
          {student.name || '—'}
        </div>

        {/* Detail rows — 2-column grid with vertical separator (matches reference) */}
        <div
          style={{
            marginTop: '2mm', flex: 1,
            display: 'grid',
            gridTemplateColumns: '13mm 0.3mm 1fr',
            columnGap: '2mm',
            rowGap: '1mm',
            paddingLeft: '5mm',  // shifted right so details sit clear of the left teal blob
            paddingBottom: '3mm',
            alignItems: 'start',
          }}
        >
          <DetailRow label="Course"     value={course?.name || student.course_name} multiline />
          <DetailRow label="ID No."     value={student.student_id} />
          <DetailRow label="Mobile"     value={student.mobile} />
          <DetailRow label="Joining"    value={formatDate(student.enrollment_date)} />
          <DetailRow label="Valid Till" value={formatDate(student.course_end_date)} highlight={ORANGE} />
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, highlight, multiline }) => (
  <>
    {/* Column 1: label, right-aligned, teal blue */}
    <div
      style={{
        textAlign: 'left',
        color: TEAL_DARK,
        fontSize: '2.4mm',
        fontWeight: 800,
        lineHeight: 1.2,
        paddingTop: '0.2mm',
      }}
    >
      {label}
    </div>
    {/* Column 2: thin vertical separator (full row height via stretch) */}
    <div
      style={{
        width: '0.3mm',
        backgroundColor: '#9CA3AF',
        alignSelf: 'stretch',
        minHeight: '3mm',
      }}
    />
    {/* Column 3: value, left-aligned, bold black (or highlight) */}
    <div
      style={{
        color: highlight || '#0f172a',
        fontSize: '2.5mm',
        fontWeight: 700,
        lineHeight: 1.2,
        paddingTop: '0.2mm',
        whiteSpace: multiline ? 'normal' : 'nowrap',
        overflow: multiline ? 'visible' : 'hidden',
        textOverflow: multiline ? 'clip' : 'ellipsis',
        wordBreak: multiline ? 'break-word' : 'normal',
      }}
    >
      {value || '—'}
    </div>
  </>
);

export default StudentIdCard;
